import { z } from 'zod';

/**
 * Request and response shapes of the social-admin JSON API, one file for the whole contract.
 *
 * Field names follow the API's own `snake_case` (social-admin/docs/api.md) rather than being
 * renamed to camelCase, so a type here can be compared to the document directly.
 */

/** An offset page, as returned by every list endpoint except the audit log. */
export function pageSchema<Item extends z.ZodType>(item: Item) {
  return z.object({
    items: z.array(item),
    page: z.number(),
    size: z.number(),
    total_items: z.number(),
    total_pages: z.number(),
  });
}
export type Page<T> = {
  items: T[];
  page: number;
  size: number;
  total_items: number;
  total_pages: number;
};

export const ADMIN_ROLES = ['SUPER_ADMIN', 'OPERATIONS', 'BILLING', 'READ_ONLY'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const adminSessionSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(ADMIN_ROLES),
  // Not live yet: social-admin will start sending this to force a change-password screen
  // before anything else. Defaults to false so today's API response still parses.
  must_change_password: z.boolean().optional().default(false),
});
export type AdminSession = z.infer<typeof adminSessionSchema>;

export interface LoginRequest {
  email: string;
  password: string;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export const queueViewSchema = z.object({
  slug: z.string(),
  label: z.string(),
  due: z.number(),
  in_progress: z.number(),
  stale: z.number(),
  healthy: z.boolean(),
  release_risk: z.string(),
});
export type QueueView = z.infer<typeof queueViewSchema>;

export const dashboardViewSchema = z.object({
  stale_after: z.string(),
  partners: z.number(),
  active_partners: z.number(),
  orgs: z.number(),
  orgs_by_status: z.record(z.string(), z.number()),
  accounts_by_platform: z.record(z.string(), z.record(z.string(), z.number())),
  accounts_needing_reauth: z.number(),
  posts_published: z.object({ last_day: z.number(), last_week: z.number() }),
  targets_failed: z.object({ last_day: z.number(), last_week: z.number() }),
  queues: z.array(queueViewSchema),
  dead_deliveries: z.number(),
});
export type DashboardView = z.infer<typeof dashboardViewSchema>;

// ---------------------------------------------------------------------------
// Partners
// ---------------------------------------------------------------------------

export const PARTNER_STATUSES = ['ACTIVE', 'SUSPENDED', 'CLOSED'] as const;
export type PartnerStatus = (typeof PARTNER_STATUSES)[number];

export const PARTNER_TYPES = ['SYSTEM', 'RESELLER'] as const;
export type PartnerType = (typeof PARTNER_TYPES)[number];

export const partnerRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.enum(PARTNER_STATUSES),
  system: z.boolean(),
});
export type PartnerRef = z.infer<typeof partnerRefSchema>;

export const OPERATING_MODES = ['DELEGATED', 'OPERATOR'] as const;
export type OperatingMode = (typeof OPERATING_MODES)[number];

export const partnerViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  type: z.enum(PARTNER_TYPES),
  status: z.enum(PARTNER_STATUSES),
  system: z.boolean(),
  operating_mode: z.enum(OPERATING_MODES),
  // Sorted; follows the mode. The permission checklist is drawn from this, never a fixed list.
  allowed_key_permissions: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PartnerView = z.infer<typeof partnerViewSchema>;

export const domainSchema = z.object({
  id: z.string(),
  hostname: z.string(),
  status: z.enum(['PENDING', 'VERIFIED', 'ACTIVE', 'DISABLED']),
  verification_token: z.string(),
  verified_at: z.string().nullable(),
  created_at: z.string(),
});
export type Domain = z.infer<typeof domainSchema>;

export const redirectUriSchema = z.object({
  id: z.string(),
  uri: z.string(),
  created_at: z.string(),
});
export type RedirectUri = z.infer<typeof redirectUriSchema>;

export const API_KEY_ENVIRONMENTS = ['LIVE', 'TEST'] as const;
export type ApiKeyEnvironment = (typeof API_KEY_ENVIRONMENTS)[number];

export const apiKeyViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  scope: z.enum(['PARTNER', 'ORG']),
  environment: z.enum(API_KEY_ENVIRONMENTS),
  key_prefix: z.string(),
  permissions: z.array(z.string()),
  status: z.enum(['ACTIVE', 'REVOKED']),
  partner_id: z.string().nullable(),
  org_id: z.string().nullable(),
  last_used_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  created_at: z.string(),
});
export type ApiKeyView = z.infer<typeof apiKeyViewSchema>;

export const issuedKeySchema = z.object({
  api_key: apiKeyViewSchema,
  one_time_secret: z.string(),
});
export type IssuedKey = z.infer<typeof issuedKeySchema>;

export const partnerKeysSchema = z.object({
  partner: partnerViewSchema,
  keys: z.array(apiKeyViewSchema),
  available_permissions: z.array(z.string()),
});
export type PartnerKeys = z.infer<typeof partnerKeysSchema>;

export const planViewSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: z.enum(['ACTIVE', 'ARCHIVED']),
  owner_partner_id: z.string().nullable(),
  owner: partnerRefSchema.nullable(),
  editable: z.boolean(),
  limits: z.object({
    social_accounts: z.number().nullable(),
    posts_per_month: z.number().nullable(),
    members: z.number().nullable(),
    workspaces: z.number().nullable(),
  }),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PlanView = z.infer<typeof planViewSchema>;
export type PlanLimits = PlanView['limits'];

export const partnerDetailSchema = z.object({
  partner: partnerViewSchema,
  domains: z.array(domainSchema),
  redirect_uris: z.array(redirectUriSchema),
  plans: z.array(planViewSchema),
});
export type PartnerDetail = z.infer<typeof partnerDetailSchema>;

export interface CreatePartnerRequest {
  name: string;
  slug: string;
}

export interface IssueKeyRequest {
  name: string;
  environment: ApiKeyEnvironment;
  permissions: string[];
}
// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export const plansResponseSchema = z.object({
  platform_plans: z.array(planViewSchema),
  partner_plans: z.array(planViewSchema),
});
export type PlansResponse = z.infer<typeof plansResponseSchema>;

export interface PlanLimitsInput {
  social_accounts: number | null;
  posts_per_month: number | null;
  members: number | null;
  workspaces: number | null;
}

export interface CreatePlanRequest {
  code: string;
  name: string;
  limits: PlanLimitsInput;
}

export interface UpdatePlanRequest {
  name: string;
  limits: PlanLimitsInput;
}
